import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, DialogPlugin, MessagePlugin, Space, Switch } from 'tdesign-react';
import { connect } from 'react-redux';
import { useMemoizedFn } from 'ahooks';
import SvgIcon from '@/components/SvgIcon';
import DisableWrapper from '@/components/DisableWrapper';
import { isVoid } from '@/utils/common';
import { fetchGeoPolice } from '@/slices/topicSlice';
import { deleteGeoPolice } from '@/services/topicService';
import PolicyForm from './PolicyForm';

// 空策略判断
export function isEmptyPolicy(data = {}) {
  return isVoid(data.dispatchThrottlingRateMsg) && isVoid(data.dispatchThrottlingRateBytes);
}

function PartitionLimiting({ geoPolice, fetchingGeoPolice, commonProps, fetchGeoPolice }) {
  // 有限流
  const hasPolice = !isEmptyPolicy(geoPolice);
  const [policyVisible, setPolicyVisible] = useState(false);

  const loadPolicy = useMemoizedFn(() => {
    fetchGeoPolice(commonProps);
  });

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const rateChange = async (value) => {
    if (value === true) {
      // 打开配置
      setPolicyVisible(true);
    } else {
      // 删除配置
      const dialog = DialogPlugin.confirm({
        header: '提示',
        theme: 'warning',
        body: <>确定删除分区限速？</>,
        confirmBtn: {
          content: '确定',
        },
        onClose: () => dialog.hide(),
        onConfirm: async () => {
          try {
            dialog.update({
              confirmBtn: { loading: true, content: '确定' },
            });
            await deleteGeoPolice(commonProps);
            MessagePlugin.success('操作成功！');
            dialog.hide();
            loadPolicy();
          } catch (error) {
            console.trace(error);
            throw error;
          } finally {
            dialog.update({
              confirmBtn: { loading: false, content: '确定' },
            });
          }
        },
      });
    }
  };

  const handleClose = (refresh) => {
    setPolicyVisible(false);
    if (refresh) {
      loadPolicy();
    }
  };

  return (
    <Root>
      <Header>
        <Space align='center' size={10}>
          <Title>分区限速</Title>
          {hasPolice && (
            <Button
              size='small'
              icon={<SvgIcon type='pencil-alt' size={18} />}
              variant='text'
              theme='primary'
              onClick={() => setPolicyVisible(true)}
            />
          )}
        </Space>
        <DisableWrapper>
          <Switch value={hasPolice} loading={fetchingGeoPolice} onChange={rateChange} />
        </DisableWrapper>
      </Header>
      <Body>
        <Item>
          <Label>速率</Label>
          {isVoid(geoPolice.dispatchThrottlingRateMsg) ? (
            '-'
          ) : (
            <Value>{geoPolice.dispatchThrottlingRateMsg}</Value>
          )}{' '}
          条/s
        </Item>
        <Item>
          <Label>吞吐</Label>
          {isVoid(geoPolice.dispatchThrottlingRateBytes) ? (
            '-'
          ) : (
            <Value>{geoPolice.dispatchThrottlingRateBytes}</Value>
          )}{' '}
          Byte/s
        </Item>
      </Body>
      <PolicyForm visible={policyVisible} handleClose={handleClose} topic={commonProps.topic} />
    </Root>
  );
}

export default connect(
  (state) => ({
    fetchingGeoPolice: state.topic.fetchingGeoPolice,
    geoPolice: state.topic.geoPolice,
  }),
  {
    fetchGeoPolice,
  },
)(PartitionLimiting);

const Root = styled.div`
  border-radius: 6px;
  width: 308px;
  border: 1px solid rgba(32, 43, 64, 0.08);
  box-sizing: border-box;
  padding: 16px;
`;

const Header = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div`
  color: rgb(13, 17, 26);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const Body = styled.div`
  padding-top: 10px;
`;

const Item = styled.div`
  color: var(--td-text-color-primary);
  margin: 6px 0;
  font-size: 12px;

  line-height: 20px;
`;

const Label = styled.span`
  color: rgba(32, 43, 64, 0.5);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  padding-right: 20px;
`;

const Value = styled.span`
  font-weight: bold;
  color: var(--td-text-color-brand);
`;
